import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2 whitespace-nowrap cursor-pointer">
              <h1 className="text-xl md:text-2xl font-heading font-bold text-primary-500">
                91视频
              </h1>
              <span className="hidden sm:inline text-sm text-foreground-600 font-label">
                高清在线观看
              </span>
            </Link>

            <div className="hidden md:flex items-center gap-1">
              <Link
                to="/"
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  location.pathname === '/'
                    ? 'text-primary-500 bg-primary-50'
                    : 'text-foreground-700 hover:text-primary-500 hover:bg-background-100'
                }`}
              >
                {t('nav.home')}
              </Link>
              {categories.slice(1, 5).map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    location.pathname.includes(`/category/${cat.slug}`)
                      ? 'text-primary-500 bg-primary-50'
                      : 'text-foreground-700 hover:text-primary-500 hover:bg-background-100'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
              <Link
                to="/about"
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  location.pathname === '/about'
                    ? 'text-primary-500 bg-primary-50'
                    : 'text-foreground-700 hover:text-primary-500 hover:bg-background-100'
                }`}
              >
                {t('nav.about')}
              </Link>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setSearchOpen(!searchOpen)}
                className="w-9 h-9 flex items-center justify-center rounded-lg text-foreground-600 hover:text-primary-500 hover:bg-background-100 transition-colors cursor-pointer"
                aria-label="搜索"
              >
                <i className="ri-search-line text-lg"></i>
              </button>
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg text-foreground-600 hover:text-primary-500 hover:bg-background-100 transition-colors cursor-pointer"
                aria-label="菜单"
              >
                <i className={`text-lg ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
              </button>
            </div>
          </div>
        </div>

        {searchOpen && (
          <div className="border-t border-background-200 bg-background-50">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-3">
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                <input
                  type="text"
                  placeholder={t('nav.search')}
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all"
                  autoFocus
                />
              </div>
            </div>
          </div>
        )}
      </nav>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-foreground-950/50" onClick={() => setMobileOpen(false)}></div>
          <div className="absolute top-16 left-0 right-0 bg-background-50 shadow-lg rounded-b-2xl py-4 px-4">
            <div className="flex flex-col gap-1">
              <Link
                to="/"
                onClick={() => setMobileOpen(false)}
                className={`px-4 py-3 rounded-lg text-sm font-medium cursor-pointer ${
                  location.pathname === '/' ? 'text-primary-500 bg-primary-50' : 'text-foreground-700'
                }`}
              >
                {t('nav.home')}
              </Link>
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => setMobileOpen(false)}
                  className={`px-4 py-3 rounded-lg text-sm font-medium cursor-pointer ${
                    location.pathname.includes(`/category/${cat.slug}`)
                      ? 'text-primary-500 bg-primary-50'
                      : 'text-foreground-700'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
              <Link
                to="/about"
                onClick={() => setMobileOpen(false)}
                className={`px-4 py-3 rounded-lg text-sm font-medium cursor-pointer ${
                  location.pathname === '/about' ? 'text-primary-500 bg-primary-50' : 'text-foreground-700'
                }`}
              >
                {t('nav.about')}
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
}